import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../hooks/usePermissions';
import PermissionGuard from './PermissionGuard';
import { 
  MdOutlineHome,
  MdOutlineDescription,
  MdOutlineCloudUpload,
  MdOutlineHelpOutline,
  MdOutlineHelp,
  MdOutlinePayment,
  MdOutlineLogout
} from 'react-icons/md';
import { GrUserAdmin } from 'react-icons/gr';
import './Sidebar.css';

const ChevronIcon = ({ rotated }) => (
  <svg 
    width="16" height="16" viewBox="0 0 20 20" fill="none"
    style={{ marginLeft: 'auto', transition: 'transform 0.2s', transform: rotated ? 'rotate(90deg)' : 'rotate(0deg)' }}
  >
    <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Sidebar = ({ showAdminSubmenu, onAdminClick, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [adminExpanded, setAdminExpanded] = useState(isAdminRoute || showAdminSubmenu || false);
  const [dashboardExpanded, setDashboardExpanded] = useState(false);
  const [clarificationsExpanded, setClarificationsExpanded] = useState(false);
  const isBulkActionsRoute = location.pathname === '/check-upload' || location.pathname === '/batch-validation' || location.pathname === '/batch-upload' || location.pathname === '/check-update' || location.pathname === '/recon-validation' || location.pathname === '/clarification-validation';
  const [bulkActionsExpanded, setBulkActionsExpanded] = useState(isBulkActionsRoute);

  useEffect(() => {
    if (isAdminRoute) {
      setAdminExpanded(true);
    }
  }, [isAdminRoute]);

  // Auto-expand/collapse dashboard submenu based on current route
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/dashboard/checks' || location.pathname === '/dashboard/drl') {
      setDashboardExpanded(true);
    } else {
      setDashboardExpanded(false);
    }
  }, [location.pathname]);

  // Auto-expand/collapse clarifications submenu based on current route
  useEffect(() => {
    if (location.pathname === '/clarifications' || location.pathname === '/clarifications/checks' || location.pathname === '/clarifications/drl') {
      setClarificationsExpanded(true);
    } else {
      setClarificationsExpanded(false);
    }
  }, [location.pathname]);

  // Auto-expand/collapse bulk actions submenu based on current route
  useEffect(() => {
    if (location.pathname === '/check-upload' || location.pathname === '/batch-validation' || location.pathname === '/batch-upload' || location.pathname === '/check-update' || location.pathname === '/recon-validation' || location.pathname === '/clarification-validation') {
      setBulkActionsExpanded(true);
    } else {
      setBulkActionsExpanded(false);
    }
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname.startsWith('/admin');
    }
    // Dashboard is active when on dashboard routes
    if (path === '/dashboard' || path === '/') {
      return location.pathname === '/' || 
             location.pathname === '/dashboard/checks' || 
             location.pathname === '/dashboard/drl';
    }
    // Checks dashboard
    if (path === '/dashboard/checks') {
      return location.pathname === '/' || location.pathname === '/dashboard/checks';
    }
    // DRL dashboard
    if (path === '/dashboard/drl') {
      return location.pathname === '/dashboard/drl';
    }
    // Highlight Checks when on /checks, /checks/new, or /checks/:id (unless source=unknown)
    if (path === '/checks') {
      const searchParams = new URLSearchParams(location.search);
      const source = searchParams.get('source');
      // Don't highlight Checks if source is 'unknown' - highlight Unknown instead
      if (source === 'unknown' && location.pathname.startsWith('/checks/')) {
        return false;
      }
      return location.pathname === '/checks' || 
             location.pathname.startsWith('/checks/');
    }
    // Highlight Unknown when on /unknown or /checks/:id with source=unknown
    if (path === '/unknown') {
      if (location.pathname === '/unknown') {
        return true;
      }
      // Also highlight Unknown when viewing a check from Unknown page
      if (location.pathname.startsWith('/checks/')) {
        const searchParams = new URLSearchParams(location.search);
        const source = searchParams.get('source');
        return source === 'unknown';
      }
      return false;
    }
    // Highlight DIT/DRL when on /dit-drl or /dit-drl/:id
    if (path === '/dit-drl') {
      return location.pathname === '/dit-drl' || 
             location.pathname.startsWith('/dit-drl/');
    }
    // Bulk Actions parent
    if (path === '/bulk-actions') {
      return location.pathname === '/check-upload' || location.pathname === '/batch-validation' || location.pathname === '/batch-upload' || location.pathname === '/check-update' || location.pathname === '/recon-validation' || location.pathname === '/clarification-validation';
    }
    // Clarifications parent
    if (path === '/clarifications') {
      return location.pathname === '/clarifications' || 
             location.pathname === '/clarifications/checks' || 
             location.pathname === '/clarifications/drl';
    }
    // Check clarifications
    if (path === '/clarifications/checks') {
      return location.pathname === '/clarifications' || location.pathname === '/clarifications/checks';
    }
    // DRL clarifications
    if (path === '/clarifications/drl') {
      return location.pathname === '/clarifications/drl';
    }
    return location.pathname === path;
  };

  const handleAdminClick = () => {
    setAdminExpanded(!adminExpanded);
    if (onAdminClick) onAdminClick();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getUserDisplayName = () => {
    if (user) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User';
    }
    return 'User';
  };

  const getUserInitials = () => {
    if (user) {
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      if (firstName && lastName) {
        return `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`;
      }
      if (user.email) {
        return user.email[0].toUpperCase();
      }
    }
    return 'U';
  };

  const getUserRole = () => {
    if (user && user.roleMeta && user.roleMeta.name) {
      // Format role: SUPER ADMIN -> Super Admin, etc.
      return user.roleMeta.name
        .split('_')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    }
    return 'User';
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        {!isCollapsed && (
          <>
            <div className="logo">
              <img src="/ebotics_icon.png" alt="Ebotics Icon" className="logo-expanded-icon" />
              <span className="logo-text">otics</span>
            </div>
            <button className="collapse-btn" onClick={onToggleCollapse}>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 20 20" 
                fill="none"
              >
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}
        {isCollapsed && (
          <button 
            className="logo-icon-btn" 
            onClick={onToggleCollapse}
            title="Click to expand"
          >
            <div className="logo-icon">
              <img src="/ebotics_icon.png" alt="Ebotics Icon" className="logo-icon-img" />
            </div>
            <svg 
              className="expand-arrow"
              width="18" 
              height="18" 
              viewBox="0 0 20 20" 
              fill="none"
            >
              <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <nav className="sidebar-nav">
        {/* Dashboard - Available to all authenticated users with submenu */}
        <div>
          <button
            className={`nav-item ${isActive('/dashboard') || isActive('/') ? 'active' : ''} ${dashboardExpanded ? 'expanded' : ''}`}
            onClick={() => {
              if (!isCollapsed) {
                setDashboardExpanded(!dashboardExpanded);
              }
              if (!dashboardExpanded) {
                navigate('/dashboard/checks');
              } else {
                navigate('/dashboard/checks');
              }
            }}
            title={isCollapsed ? 'Dashboard' : ''}
          >
            <MdOutlineHome className="nav-icon" size={18} />
            {!isCollapsed && (
              <>
                <span>Dashboard</span>
                <ChevronIcon rotated={dashboardExpanded} />
              </>
            )}
          </button>
          {!isCollapsed && dashboardExpanded && (
            <div className="submenu-container">
              <PermissionGuard permission={PERMISSIONS.CHECK_DASHBOARD_VIEW}>
                <Link 
                  to="/dashboard/checks" 
                  className={`submenu-item ${isActive('/dashboard/checks') ? 'active' : ''}`}
                  onClick={() => navigate('/dashboard/checks')}
                >
                  Checks
                </Link>
              </PermissionGuard>
              <PermissionGuard permission={PERMISSIONS.DIT_DRL_DASHBOARD_VIEW}>
                <Link 
                  to="/dashboard/drl" 
                  className={`submenu-item ${isActive('/dashboard/drl') ? 'active' : ''}`}
                  onClick={() => navigate('/dashboard/drl')}
                >
                  Dit/Drl
                </Link>
              </PermissionGuard>
            </div>
          )}
        </div>

        {/* Checks - Requires CHECK_DASHBOARD_VIEW permission */}
        <PermissionGuard permission={PERMISSIONS.CHECK_DASHBOARD_VIEW}>
          <Link to="/checks" className={`nav-item ${isActive('/checks') ? 'active' : ''}`} title={isCollapsed ? 'Checks' : ''}>
            <MdOutlineDescription className="nav-icon" size={18} />
            {!isCollapsed && <span>Checks</span>}
          </Link>
        </PermissionGuard>

        {/* Bulk Actions - Check Upload + Batch Validation */}
        <PermissionGuard permission={PERMISSIONS.BULK_IMPORT_JOB_CREATE}>
          <div>
            <button
              className={`nav-item ${isActive('/bulk-actions') ? 'active' : ''} ${bulkActionsExpanded ? 'expanded' : ''}`}
              onClick={() => {
                if (!isCollapsed) {
                  setBulkActionsExpanded(!bulkActionsExpanded);
                }
                if (!bulkActionsExpanded) {
                  navigate('/check-upload');
                } else {
                  navigate('/check-upload');
                }
              }}
              title={isCollapsed ? 'Bulk Actions' : ''}
            >
              <MdOutlineCloudUpload className="nav-icon" size={18} />
              {!isCollapsed && (
                <>
                  <span>Bulk Actions</span>
                  <ChevronIcon rotated={bulkActionsExpanded} />
                </>
              )}
            </button>
            {!isCollapsed && bulkActionsExpanded && (
              <div className="submenu-container">
                <Link 
                  to="/check-upload" 
                  className={`submenu-item ${location.pathname === '/check-upload' ? 'active' : ''}`}
                >
                  Check Upload
                </Link>
                <Link 
                  to="/batch-validation" 
                  className={`submenu-item ${location.pathname === '/batch-validation' ? 'active' : ''}`}
                >
                  Batch Validation
                </Link>
                <Link
                  to="/batch-upload"
                  className={`submenu-item ${location.pathname === '/batch-upload' ? 'active' : ''}`}
                >
                  Batch Upload
                </Link>
                <Link
                  to="/check-update"
                  className={`submenu-item ${location.pathname === '/check-update' ? 'active' : ''}`}
                >
                  Check Update
                </Link>
                <Link
                  to="/recon-validation"
                  className={`submenu-item ${location.pathname === '/recon-validation' ? 'active' : ''}`}
                >
                  Recon Validation
                </Link>
                <PermissionGuard permission={PERMISSIONS.CLARIFICATION_VALIDATION_VIEW}>
                  <Link
                    to="/clarification-validation"
                    className={`submenu-item ${location.pathname === '/clarification-validation' ? 'active' : ''}`}
                  >
                    Clarification Validation
                  </Link>
                </PermissionGuard>
              </div>
            )}
          </div>
        </PermissionGuard>

        {/* Clarifications - Requires permission with submenu */}
        <PermissionGuard permission={[
          PERMISSIONS.CHECK_CLARIFICATION_DASHBOARD_VIEW,
          PERMISSIONS.DIT_DRL_CLARIFICATION_DASHBOARD_VIEW
        ]}>
          <div>
            <button
              className={`nav-item ${isActive('/clarifications') ? 'active' : ''} ${clarificationsExpanded ? 'expanded' : ''}`}
              onClick={() => {
                if (!isCollapsed) {
                  setClarificationsExpanded(!clarificationsExpanded);
                }
                if (!clarificationsExpanded) {
                  navigate('/clarifications/checks');
                } else {
                  navigate('/clarifications/checks');
                }
              }}
              title={isCollapsed ? 'Clarifications' : ''}
            >
              <MdOutlineHelpOutline className="nav-icon" size={18} />
              {!isCollapsed && (
                <>
                  <span>Clarifications</span>
                  <ChevronIcon rotated={clarificationsExpanded} />
                </>
              )}
            </button>
            {!isCollapsed && clarificationsExpanded && (
              <div className="submenu-container">
                <PermissionGuard permission={PERMISSIONS.CHECK_CLARIFICATION_DASHBOARD_VIEW}>
                  <Link 
                    to="/clarifications/checks" 
                    className={`submenu-item ${isActive('/clarifications/checks') ? 'active' : ''}`}
                    onClick={() => navigate('/clarifications/checks')}
                  >
                    Check
                  </Link>
                </PermissionGuard>
                <PermissionGuard permission={PERMISSIONS.DIT_DRL_CLARIFICATION_DASHBOARD_VIEW}>
                  <Link 
                    to="/clarifications/drl" 
                    className={`submenu-item ${isActive('/clarifications/drl') ? 'active' : ''}`}
                    onClick={() => navigate('/clarifications/drl')}
                  >
                    Dit/Drl
                  </Link>
                </PermissionGuard>
              </div>
            )}
          </div>
        </PermissionGuard>

        {/* Unknown - Requires CHECK_DASHBOARD_VIEW permission */}
        <PermissionGuard permission={PERMISSIONS.CHECK_DASHBOARD_VIEW}>
          <Link to="/unknown" className={`nav-item ${isActive('/unknown') ? 'active' : ''}`} title={isCollapsed ? 'Unknown' : ''}>
            <MdOutlineHelp className="nav-icon" size={18} />
            {!isCollapsed && <span>Unknown</span>}
          </Link>
        </PermissionGuard>

        {/* DIT/DRL Payments - Requires DIT_DRL_DASHBOARD_VIEW permission */}
        <PermissionGuard permission={PERMISSIONS.DIT_DRL_DASHBOARD_VIEW}>
          <Link to="/dit-drl" className={`nav-item ${isActive('/dit-drl') ? 'active' : ''}`} title={isCollapsed ? 'DIT/DRL Payments' : ''}>
            <MdOutlinePayment className="nav-icon" size={18} />
            {!isCollapsed && <span>DIT/DRL Payments</span>}
          </Link>
        </PermissionGuard>
      </nav>

      <div className="sidebar-divider"></div>

      {/* Admin Portal - Requires any admin portal permission */}
      <PermissionGuard permission={[
        PERMISSIONS.ADMIN_PORTAL_DASHBOARD_VIEW,
        PERMISSIONS.USER_LIST,
        PERMISSIONS.ROLE_LIST,
        PERMISSIONS.PRACTICE_LIST
      ]}>
        <div className="admin-section">
          <button 
            className={`nav-item admin-portal ${isActive('/admin') ? 'active' : ''}`}
            onClick={handleAdminClick}
            title={isCollapsed ? 'Admin Portal' : ''}
          >
            <GrUserAdmin className="nav-icon" size={18} />
            {!isCollapsed && <span>Admin Portal</span>}
          </button>
        </div>
      </PermissionGuard>

      <div className="sidebar-footer">
        {!isCollapsed && (
          <Link to="/profile" className="user-profile-link">
            <div className="user-profile">
              <div className="user-avatar">{getUserInitials()}</div>
              <div className="user-info">
                <div className="user-name">{getUserDisplayName()}</div>
                <div className="user-role">{getUserRole()}</div>
              </div>
            </div>
          </Link>
        )}
        {isCollapsed && (
          <Link to="/profile" className="user-profile-link-collapsed" title="Profile">
            <div className="user-profile-collapsed">
              <div className="user-avatar">{getUserInitials()}</div>
            </div>
          </Link>
        )}
        <button className="sign-out-btn" onClick={handleLogout} title={isCollapsed ? 'Sign Out' : ''}>
          <MdOutlineLogout className="nav-icon" size={18} />
          {!isCollapsed && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;

